import { useCallback, useState } from "react";
import {
  Direction,
  Flex,
  InfoBox,
  OverlayStyle,
  QueryLoader,
  SlideoutDrawerV2,
} from "@jsq/ds/core";
import { useFeatureFlag } from "@jsq/flags-react";
import { toGlobalId } from "~/utils/graphql-helpers";
import Titlebar, { TitlebarAction } from "../../components/Titlebar";
import { useAgGridUtils } from "../../utils/gridUtils";
import AddUserModal from "../components/AddUserModal";
import RemoveUserModal from "../components/RemoveUserModal";
import { WorkspaceData, WorkspaceUserData } from "../types";
import { WorkspaceUserTable } from "./components/WorkspaceUserTable";
import { useFetchArenaAccesses } from "./hooks";
import * as styles from "./styles_vanilla.css";

export type Props = {
  isOpen: boolean;
  onClose: () => void;
  workspaceData?: WorkspaceData;
};

export const WorkspaceDetailsDrawer = ({ isOpen, onClose, workspaceData }: Props) => {
  const { rowData, useGetWorkspaceUsersQueryResult } = useFetchArenaAccesses({
    arenaId: toGlobalId("ArenaObjectType", workspaceData?.id ?? -1),
  });
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const [isRemoveUserModalOpen, setIsRemoveUserModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<WorkspaceUserData | null>(null);
  const isBulkArenaAccessEnabled = useFeatureFlag("faBulkArenaAccess", false);

  const handleRemoveUser = useCallback((user: WorkspaceUserData | null) => {
    setSelectedUser(user);
    setIsRemoveUserModalOpen(true);
  }, []);

  const { onGridReady, resetFilters, isFilterPresent, onFilterChanged } =
    useAgGridUtils<WorkspaceUserData>();

  const workspaceUserTableActions: TitlebarAction[] = [
    {
      show: isFilterPresent,
      buttonProps: {
        variant: "neutral",
        icon: "times",
        key: "clear_filters",
        onClick: resetFilters,
        children: "Clear filters",
      },
    },
    {
      show: true,
      buttonProps: {
        variant: "primary",
        icon: "plus",
        key: "assign_user",
        onClick: () => setIsAddUserModalOpen(true),
        children: "Assign user",
      },
    },
  ];

  return (
    <>
      <SlideoutDrawerV2
        isOpen={isOpen}
        onClose={onClose}
        direction={Direction.Right}
        isDismissable={true}
        overlayStyle={OverlayStyle.Transparent}
      >
        <Flex alignItems="center" justifyContent="center" className={styles.drawerStyles}>
          <QueryLoader result={useGetWorkspaceUsersQueryResult}>
            <Flex flexDirection="column" className={styles.drawerContentWrapper}>
              <SlideoutDrawerV2.ComplexHeader title={workspaceData?.name ?? ""} />
              <SlideoutDrawerV2.Body className={styles.drawerBody}>
                <Titlebar
                  marginBottom="lg"
                  title="Assigned users"
                  variant="h3"
                  actionButtons={workspaceUserTableActions}
                />
                {isBulkArenaAccessEnabled && (
                  <InfoBox
                    className={styles.infoBanner}
                    variant="info"
                    title={
                      'Users with "Access to all client workspaces" enabled will also have access to this workspace, even if they are not listed here.'
                    }
                  />
                )}
                <WorkspaceUserTable
                  onGridReady={onGridReady}
                  rowData={rowData}
                  onRemoveUser={handleRemoveUser}
                  onFilterChanged={onFilterChanged}
                />
              </SlideoutDrawerV2.Body>
            </Flex>
          </QueryLoader>
        </Flex>
        <AddUserModal
          selectedWorkspace={workspaceData}
          isOpen={isAddUserModalOpen}
          onClose={() => setIsAddUserModalOpen(false)}
        />
        <RemoveUserModal
          isOpen={isRemoveUserModalOpen}
          onClose={() => setIsRemoveUserModalOpen(false)}
          workspaceData={workspaceData}
          userData={selectedUser}
        />
      </SlideoutDrawerV2>
    </>
  );
};

export default WorkspaceDetailsDrawer;
